<template>
  <div style="width: 100%">
    <div
      class="section-header d-flex justify-content-between align-items-center p-1"
    >
      <p class="mb-0" style="font-weight: 1000">Admin Users</p>
      <div class="d-flex align-items-center">
        <b-form-input
          v-model="searchQuery"
          placeholder="Search users..."
          style="width: 250px; margin-right: 6px"
        ></b-form-input>
        <b-button
          variant="success"
          size="sm"
          class="d-flex align-items-center"
          style="height: 38px"
          @click="handleShowModal"
        >
          <feather-icon
            icon="PlusIcon"
            size="16"
            class="align-middle text-white mr-1"
          />
          Add New User
        </b-button>
      </div>
    </div>

    <b-table
      ref="refAdminListsTable"
      :items="fetchAdminList"
      :fields="tableColumns"
      responsive
      striped
      show-empty
      primary-key="campaign"
    >
      <template #empty>
        <div v-if="loadingAdminList" class="text-center text-dark my-2">
          <p>Loading... Please wait</p>
        </div>

        <div
          v-else-if="!loadingAdminList && fetchAdminList.length === 0"
          class="text-center text-dark my-2"
        >
          No matching records found
        </div>
      </template>

      <!-- Column: name -->
      <template #cell(name)="{ item }">
        <div class="d-flex align-items-center" style="font-weight: 900">
          <b-avatar
            variant="secondary"
            class="mr-1"
            style="background: gray; width: 30px; height: 30px"
          >
            <feather-icon icon="UsersIcon" size="18" />
          </b-avatar>
          {{
            `${item.firstName}${
              item.middleName ? ` ${item.middleName} ` : " "
            }${item.lastName}`
          }}
        </div>
      </template>

      <!-- Column: role -->
      <template #cell(role)="{ item }">
        <b-media v-if="item.roles.length > 0 && item.roles[0] && item.roles[0].name" vertical-align="center">
          <b-badge pill :variant="getRoleBadgeClass(item.roles[0].roleId)">
            {{ item.roles[0].name }}
          </b-badge>
        </b-media>
      </template>

      <!-- Column: lastLogin -->
      <template #cell(lastLogin)="{ item }">
        <div class="d-flex align-items-center font-weight-bold id-column">
          <div class="d-flex align-items-center font-weight-extrabold">
            {{ formatTime(item.lastLoginDate, "yyyy-MM-dd hh:mm aa") }}
          </div>
        </div>
      </template>

      <!-- Column: createdDate -->
      <template #cell(createdDate)="{ item }">
        <div class="d-flex align-items-center font-weight-bold id-column">
          <div class="d-flex align-items-center font-weight-extrabold">
            {{ formatTime(item.createdTime, "yyyy-MM-dd") }}
          </div>
        </div>
      </template>

        <!-- Column: Status -->
        <template #cell(status)="{ item }">
          <b-media vertical-align="center">
            <b-badge class="text-capitalize" pill :variant="item.status  === 'active'? 'success' : 'danger'">
              {{ item.status  }}
            </b-badge>
          </b-media>
        </template>

      <!-- Column: actions -->
      <template #cell(actions)="{ item }">
        <div
          class="d-flex align-items-center font-weight-bold"
          style="gap: 10px"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-shield cursor-pointer"
            style="color: #9810fa; width: 16px; height: 16px"
            aria-hidden="true"
            @click="handleEditRole(item)"
          >
            <path
              d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
            ></path>
          </svg>
          <feather-icon
            icon="EditIcon"
            size="16"
            class="align-middle text-warning cursor-pointer"
            @click="handleEditUser(item)"
          />
          <feather-icon
            icon="TrashIcon"
            size="16"
            class="align-middle cursor-pointer"
            style="color: red"
            @click="showModal('delete', item)"
          />
        </div>
      </template>
    </b-table>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">
            Showing {{ dataMeta.from }} to {{ dataMeta.to }} of
            {{ dataMeta.of }} admins
          </span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalAdmins"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>

    <!-- ADD/UPDATE ADMIN -->
    <b-modal
      ref="user"
      :title="modalTitle"
      centered
      hide-footer
      cancel-variant="outline-secondary"
      @hide="hideModal"
    >
      <section style="color: black">
        <!-- Form: Information Form -->
        <validation-observer ref="simpleRules">
          <b-form class="mt-1">
            <b-row class="">
              <!-- Title -->
              <b-col cols="12">
                <b-form-group>
                  <div class="d-flex">
                    <label for="title">Title</label>
                  </div>

                  <validation-provider
                    #default="{ errors }"
                    name="title"
                    vid="title"
                    rules="required"
                  >
                    <b-form-radio-group
                      id="title"
                      v-model="dataUser.title"
                      :options="titleOptions"
                      value="Mr"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- First Name -->
              <b-col cols="12" md="6">
                <b-form-group>
                  <div class="d-flex">
                    <label for="first name">First Name</label>
                  </div>

                  <validation-provider
                    #default="{ errors }"
                    name="firstName"
                    vid="firstName"
                    rules="required"
                  >
                    <b-form-input v-model="dataUser.firstName" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Last Name -->
              <b-col cols="12" md="6">
                <b-form-group>
                  <div class="d-flex">
                    <label for="last name">Last Name</label>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    name="lastName"
                    vid="lastName"
                    rules="required"
                  >
                    <b-form-input v-model="dataUser.lastName" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Email -->
              <b-col cols="12" md="6">
                <b-form-group>
                  <div class="d-flex">
                    <label for="email">Email</label>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    name="email"
                    vid="email"
                    rules="required|email"
                  >
                    <b-form-input v-model="dataUser.email" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Phone Number -->
              <b-col cols="12" md="6">
                <b-form-group>
                  <div class="d-flex">
                    <label for="phone number">Phone Number</label>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    name="phoneNumber"
                    vid="phoneNumber"
                    rules="required|phone"
                  >
                    <b-form-input
                      v-model="dataUser.phoneNumber"
                      type="tel"
                      maxlength="10"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Password -->
              <b-col cols="12" md="6">
                <b-form-group label="Password" label-for="password">
                  <validation-provider
                    #default="{ errors }"
                    name="Password"
                    vid="Password"
                    rules="required|min:6|password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        v-model="dataUser.password"
                        :type="passwordTypeNew"
                        name="new-password"
                        placeholder="New Password"
                        autocomplete="new-password"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="passwordToggleIconNew"
                          class="cursor-pointer"
                          @click="togglePasswordNew"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Confirm password -->
              <b-col cols="12" md="6">
                <b-form-group
                  label="Confirm Password"
                  label-for="confirm-password"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Confirm Password"
                    rules="required|confirmed:Password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        v-model="dataUser.confirmPassword"
                        :type="passwordConfirm"
                        name="confirm-password"
                        placeholder="Confirm Password"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="passwordToggleIconConfirm"
                          class="cursor-pointer"
                          @click="togglePasswordConfirm"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Birth Date -->
              <b-col cols="12" md="6">
                <b-form-group label="Birth Date" label-for="birth-date">
                  <validation-provider
                    #default="{ errors }"
                    rules="required"
                    name="dob"
                    vid="dob"
                  >
                    <flat-pickr
                      v-model="dataUser.dob"
                      class="form-control"
                      :config="configDate"
                      placeholder="DD-MM-YYYY"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <!-- USER Name -->
              <b-col cols="12" md="6">
                <b-form-group>
                  <div class="d-flex">
                    <label for="first name">Username</label>
                  </div>

                  <validation-provider
                    #default="{ errors }"
                    name="userName"
                    vid="userName"
                    rules="required"
                  >
                    <b-form-input v-model="dataUser.userName" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Address -->
              <!-- <b-col>
                <b-form-group label="Address" label-for="address">
                  <validation-provider
                    #default="{ errors }"
                    rules="required"
                    name="fullAddress"
                    vid="fullAddress"
                  >
                    <vue-google-autocomplete
                      id="map"
                      ref="fullAddress"
                      v-model="fullAddress"
                      class="form-control"
                      placeholder="Full Address"
                      country="aus"
                      :fields="fields"
                      @placechanged="getAddressData"
                      @inputChange="changeInput"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col> -->

              <!-- Assign Role -->
              <b-col cols="12">
                <b-form-group>
                  <validation-provider
                    #default="{ errors }"
                    name="role"
                    vid="role"
                    rules="required"
                  >
                    <label>Assign Role</label>
                    <b-form-select
                      v-model="dataUser.role"
                      :options="optionsRole.map((i) => ({
                        text: i.name,
                        value: i.value,
                      }))"
                    >
                      <template #first>
                        <b-form-select-option :value="null" disabled
                          >Select a role</b-form-select-option
                        >
                      </template>
                    </b-form-select>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Status -->
              <b-col cols="12" md="6">
                <label class="mb-1">Status</label>
                <b-form-radio-group
                  id="active-status"
                  v-model="dataUser.isActive"
                  :options="optionsAdminStatus"
                  name="active-status"
                />
              </b-col>
            </b-row>

            <b-row class="mt-1" style="border-top: 1px solid gray">
              <b-col class="d-flex justify-content-between mt-1">
                <b-button
                  variant="outline-secondary"
                  @click="$refs.user.hide()"
                >
                  Cancel
                </b-button>
                <template>
                  <b-button
                    v-if="modalTitle === 'Add New Admin User'"
                    variant="primary"
                    class="mb-1 mb-sm-0"
                    :block="$store.getters['app/currentBreakPoint'] === 'xs'"
                    :disabled="loadingCreateAcc"
                    @click="handleSubmitModal"
                  >
                    <Spinner v-if="loadingCreateAcc" />
                    <template v-else> Create Admin </template>
                  </b-button>
                  <b-button
                    v-else
                    variant="primary"
                    class="mb-1 mb-sm-0"
                    :block="$store.getters['app/currentBreakPoint'] === 'xs'"
                    :disabled="loadingCreateAcc"
                    @click="handleSubmitModalUpdate"
                  >
                    <Spinner v-if="loadingCreateAcc" />
                    <template v-else> Update </template>
                  </b-button>
                </template>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </section>
    </b-modal>

    <!-- CONFIRM MODAL -->
    <b-modal
      ref="modal"
      :title="modal.title"
      centered
      cancel-variant="outline-secondary"
      :ok-disabled="isSubmitting"
      @ok="handleSubmitModalConfirm"
    >
      <b-card-text class="text-title">
        {{ modal.content }}
      </b-card-text>
      <template #modal-ok>
        <Spinner v-if="isSubmitting" />
        <span v-else>Submit</span>
      </template>
    </b-modal>

    <!-- CHANGE ROLE MODAL -->
    <b-modal
      ref="role"
      :title="titleRole"
      centered
      cancel-variant="outline-secondary"
      hide-footer
    >
      <div v-if="userUpdate">
        <div
          v-if="userUpdate"
          class="d-flex align-items-center p-1 my-1"
          style="color: #000; background: #f9fafb; border-radius: 12px"
        >
          <b-avatar variant="secondary" class="mr-2" style="background: gray">
            <feather-icon icon="UsersIcon" size="22" />
          </b-avatar>
          <div class="d-flex flex-column">
            <span class="font-weight-bold" style="font-size: 16px">{{
              `${userUpdate.firstName}${
                userUpdate.middleName ? ` ${userUpdate.middleName} ` : " "
              }${userUpdate.lastName}`
            }}</span>
            <span>{{ userUpdate.email }}</span>
            <span style="margin-top: 5px" v-if="userUpdate.roles.length > 0"
              >Current Role:
              <b-badge
                pill
                :variant="getRoleBadgeClass(userUpdate.roles[0].roleId)"
              >
                {{ userUpdate.roles[0].name }}
              </b-badge></span
            >
          </div>
        </div>
        <p style="color: #000; font-size: 13px" class="m-0 font-weight-bold">
          Select New Role
        </p>
        <b-row style="color: #000">
          <b-col cols="12">
            <RoleSelect v-model="newUserRole" :options="optionsRole" @input="handleChangeRole"/>
          </b-col>
          <!-- // fff085 -->
          <b-col
            v-if="
              userUpdate &&
              newUserRole &&
              optionsRole.find((i) => i.value === newUserRole).value !==
                userUpdate.roles[0].roleId
            "
            cols="12"
          >
            <div
              class="mt-1"
              style="
                color: #a65f00;
                border: 2px solid #fff085;
                border-radius: 12px;
                background: #fefce8;
                font-size: 13px;
                padding: 10px;
              "
            >
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-triangle-alert h-4 w-4 text-yellow-600"
                  aria-hidden="true"
                >
                  <path
                    d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"
                  ></path>
                  <path d="M12 9v4"></path>
                  <path d="M12 17h.01"></path>
                </svg>
                <span style="margin-left: 5px">Role Change Preview</span>
              </div>
              <p class="m-0">
                Changing role from
                <span class="font-weight-bold">{{
                  userUpdate.roles[0].name
                }}</span>
                to
                <span class="font-weight-bold">{{
                  optionsRole.find((i) => i.value === newUserRole).name
                }}</span>
                will update all permissions according to the new role template.
              </p>
            </div>
          </b-col>
        </b-row>
        <b-row v-if="optionsRole && userUpdate" class="mt-1" style="border-top: 1px solid gray">
          <b-col class="d-flex justify-content-end mt-1">
            <b-button
              style="padding: 5px 10px; margin-right: 10px"
              variant="outline-secondary"
              @click="$refs.role.hide()"
            >
              Cancel
            </b-button>
            <b-button
              variant="primary"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
              :disabled="
             optionsRole.find((i) => i.value === newUserRole) &&   optionsRole.find((i) => i.value === newUserRole).code ===
                userUpdate.roles[0].code
              "
              @click="handleSubmitChangeUserRole"
              style="padding: 8px 10px"
            >
              <Spinner v-if="loadingCreateAcc" />
              <template v-else>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-shield"
                  style="
                    width: 16px;
                    height: 16px;
                    margin-top: -5px;
                    margin-right: 4px;
                  "
                  aria-hidden="true"
                >
                  <path
                    d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                  ></path>
                </svg>
                Update Role
              </template>
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { getUserData } from "@/auth/utils";
import { isRoleSuperAdmin } from "@/helpers/permission";
import useToastHandler from "@/services/toastHandler";
import { avatarText } from "@core/utils/filter";
import {
  BTable,
  VBModal,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BCol,
  BRow,
  BButton,
  BFormInput,
  BFormGroup,
  BForm,
  BInputGroupAppend,
  BInputGroup,
  BFormSelect,
  BFormSelectOption,
  BFormRadioGroup,
  BMedia,
  BCardText,
  BAvatar,
} from "bootstrap-vue";
import { mapActions, mapState } from "vuex";
import {
  formatTime,
  formatDate,
  formatCurrencyBuiltInFunc,
  formatDateTimeLocal,
  formatDateTimeTracking,
} from "@/helpers/utils";
import { formatDistanceStrict } from "date-fns";
import { computed, ref, watch } from "@vue/composition-api";
import { objectProp } from "@/helpers/props";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { required, email, password, min, phone, confirmed } from "@validations";
import VueGoogleAutocomplete from "vue-google-autocomplete";
import flatPickr from "vue-flatpickr-component";
import { calculateDate18YearsAgo } from "@core/utils/enum/date";
import Spinner from "@core/components/Spinner";
import debounce from "debounce";
import RoleSelect from "./RoleSelect.vue";

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BCol,
    BRow,
    BButton,
    BFormInput,
    BFormGroup,
    BForm,
    BInputGroupAppend,
    BInputGroup,
    BFormSelect,
    BFormSelectOption,
    BFormRadioGroup,
    BMedia,
    VueGoogleAutocomplete,
    flatPickr,
    Spinner,
    BCardText,
    BAvatar,
    RoleSelect,
  },
  directives: {
    "b-modal": VBModal,
  },
  props: {
    fieldFilter: objectProp(),
    orderFilter: objectProp(),
    statusFilter: objectProp(),
  },
  data() {
    return {
      required,
      min,
      confirmed,
      email,
      password,
      phone,
      deleteId: null,
      tableColumns: [
        { key: "userId", label: "id" },
        { key: "username", },
        { key: "name" },
        { key: "email" },
        { key: "role" },
        { key: "lastLogin" },
        { key: "createdDate" },
        { key: "status" },
        { key: "actions" },
      ],
      modalTitle: "",
      dataUser: {
        title: "Mr",
        firstName: "",
        lastName: "",
        email: "",
        role: null,
        phoneNumber: "",
        password: "",
        confirmPassword: "",
        isActive: true,
        dob: null,
        userName: "",
      },
      passwordTypeNew: "password",
      passwordConfirm: "password",
      optionsRole: [],
      optionsAdminStatus: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      // fields: [
      //   "address_components",
      //   "adr_address",
      //   "alt_id",
      //   "formatted_address",
      //   "geometry",
      //   "id",
      //   "name",
      //   "scope",
      //   "type",
      //   "vicinity",
      // ],
      fullAddress: "",
      suburb: "",
      state: "",
      postCode: "",
      country: "AUS",
      streetName: "",
      streetNumber: "",
      streetType: "",
      titleOptions: [
        { text: "Mr", value: "Mr" },
        { text: "Ms", value: "Ms" },
        { text: "Mrs", value: "Mrs" },
        { text: "Miss", value: "Miss" },
        { text: "Dr", value: "Dr" },
      ],
      configDate: {
        altFormat: "d-m-Y",
        altInput: true,
        maxDate: this.calculateDate18YearsAgo(),
        static: true,
      },
      type: "",
      modal: {
        title: "",
        content: "",
      },
      userUpdate: null,
      newUserRole: null,
      titleRole: "",
    };
  },
  computed: {
    ...mapState("user/admin", [
      "loadingCreateAcc",
      "loadingDeleteAcc",
      "loadingAdminList",
      "pagination",
    ]),
    passwordToggleIconNew() {
      return this.passwordTypeNew === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    passwordToggleIconConfirm() {
      return this.passwordConfirm === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    isSubmitting() {
      return this.loadingDeleteAcc || this.loadingCreateAcc;
    },
  },
  watch: {},
  async created() {
    const res = await this.getRoleList({
      page: 1,
      limit: 100,
    });
    this.optionsRole = res.data.map((item) => ({
      name: item.name,
      value: item.roleId,
      description: item.description,
      // code: item.code,
    }));
  },

  methods: {
    ...mapActions({
      getAdminList: "user/admin/getAdminList",
      getRoleList: "user/admin/getRoleList",
      createAdmin: "user/admin/createAdmin",
      setUserRole: "user/admin/setUserRole",
      deleteUser: "user/admin/deleteUser",
      updateInfoAdmin: "user/admin/updateInfoAdmin",
      getTotalAdmin: "user/statistic/getTotalAdmin",
    }),
    formatColorType(val) {
      if (val === "deposit") return "light-success";
      if (val === "direct") return "light-danger";
      return "light-warning";
    },
    async fetchAdminList() {
      const data = await this.getAdminList({
        isAdminUser: 1,
        page: this.currentPage,
        limit: this.perPage,
        ...(this.searchQuery && { search: this.searchQuery }),
      });

      this.totalAdmins = this.pagination.total;

      if (!data) return this.showToastError(false, "Error fetching offer list");
      return data;
    },
    async handleSubmitModal() {
      const valid = await this.$refs.simpleRules.validate();
      if (!valid) return;
      const data = {
        phoneNumber: `+61${this.dataUser.phoneNumber?.slice(1, 10)}`,
        firstName: this.dataUser.firstName,
        lastName: this.dataUser.lastName,
        dob: this.dataUser.dob,
        title: this.dataUser.title,
        gender: this.dataUser.title === "Mr" ? "M" : "F",
        email: this.dataUser.email,
        password: this.dataUser.password,
        status: this.dataUser.isActive ? "active" : "inactive",
        roleId: this.dataUser.role,
        username: this.dataUser.userName,
      };
      const res = await this.createAdmin(data);
      if (res.data) {
        // await this.setUserRole({
        //   userId: res.data.userId,
        //   roleId: this.dataUser.role,
        // });
        this.showToastSuccess(true, "New admin user created successfully!");
        this.refetchData();
        this.getTotalAdmin();
      }
      this.$refs.user.hide();
      this.resetModal();
    },
    handleShowModal() {
      this.modalTitle = "Add New Admin User";
      this.$refs.user.show();
    },
    togglePasswordNew() {
      this.passwordTypeNew =
        this.passwordTypeNew === "password" ? "text" : "password";
    },
    togglePasswordConfirm() {
      this.passwordConfirm =
        this.passwordConfirm === "password" ? "text" : "password";
    },
    getRoleBadgeClass(role) {
      const classes = {
        1: "light-primary",
        2: "light-success",
        3: "light-warning text-dark",
        4: "light-secondary",
        5: "light-info",
      };
      return classes[role] || "light-secondary";
    },
    // getAddressData(addressData, place) {
    //   this.fullAddress = place.formatted_address;
    //   const routeItem = place.address_components.find((item) =>
    //     item.types.includes("route")
    //   );

    //   const streetLongName = routeItem?.long_name.split(" ");
    //   this.streetType = streetLongName?.pop() || "";
    //   this.streetName = streetLongName.join(" ") || "";
    //   const postCodeItem = place.address_components.find((item) =>
    //     item.types.includes("postal_code")
    //   );
    //   this.postCode = postCodeItem?.long_name || "";
    //   const streetNumberItem = place.address_components.find((item) =>
    //     item.types.includes("street_number")
    //   );
    //   this.streetNumber = streetNumberItem?.long_name || "";
    //   const subpremiseItem = place.address_components.find((item) =>
    //     item.types.includes("subpremise")
    //   );
    //   this.unitNumber = subpremiseItem?.long_name || "";
    //   const stateItem = place.address_components.find((item) =>
    //     item.types.includes("administrative_area_level_1")
    //   );
    //   this.state = stateItem?.long_name || "";
    //   const cityItem = place.address_components.find((item) =>
    //     item.types.includes("locality")
    //   );
    //   this.suburb = cityItem?.long_name || "";
    // },
    changeInput(e) {
      if (e.newVal === "") this.fullAddress = null;
    },
    hideModal() {
      this.dataUser = {
        title: "Mr",
        firstName: "",
        lastName: "",
        email: "",
        role: null,
        phoneNumber: "",
        password: "",
        confirmPassword: "",
        isActive: true,
        dob: null,
        userName: "",
      };
      this.passwordTypeNew = "password";
      this.passwordConfirm = "password";
      this.modalTitle = "";
    },
    handleEditUser(item) {
      this.userUpdate = item;
      this.modalTitle = "Edit Admin User";
      this.dataUser = {
        title: item.title,
        firstName: item.firstName,
        lastName: item.lastName,
        email: item.email,
        role: item.roles[0].roleId,
        phoneNumber: item.phoneNumber?.replace("+61", "0") || null,
        password: "",
        confirmPassword: "",
        isActive: item.status === "active",
        dob: item.dob,
        userName: item.username,
      };
      this.$refs.user.show();
    },
    setModal(title, content) {
      this.modal.title = title;
      this.modal.content = content;
    },
    showModal(type, item) {
      this.action = { item, type };

      if (type === "delete")
        this.setModal(
          "Delete User",
          `Are you sure you want to delete admin ${item.firstName} ${item.lastName}?`
        );

      this.$refs.modal.show();
    },
    async handleSubmitModalConfirm(event) {
      event.preventDefault();
      const { item, type } = this.action;
      if (type === "delete") await this.handleDeleteAdmin(item);

      if (!this.isSubmitting) {
        this.$nextTick(() => {
          this.$refs.modal.hide();
        });
      }
    },
    async handleDeleteAdmin(item) {
      const result = await this.deleteUser(item.userId);
      if (!result) return;
      this.showToastSuccess("Success", "Delete user successfully!");
      this.refetchData();
      this.getTotalAdmin();
    },
    async handleSubmitModalUpdate() {
      const valid = await this.$refs.simpleRules.validate();
      if (!valid) return;
      const data = {
        phoneNumber: `+61${this.dataUser.phoneNumber?.slice(1, 10)}`,
        firstName: this.dataUser.firstName,
        lastName: this.dataUser.lastName,
        dob: this.dataUser.dob,
        title: this.dataUser.title,
        gender: this.dataUser.title === "Mr" ? "M" : "F",
        email: this.dataUser.email,
        newPassword: this.dataUser.password,
        status: this.dataUser.isActive ? "active" : "inactive",
        username: this.dataUser.userName,
        ...(this.userUpdate.roles[0].roleId !== this.dataUser.role && {
          roleId: this.dataUser.role,
        }),
      };
      const res = await this.updateInfoAdmin({
        userId: this.userUpdate.userId,
        ...data,
      });
      if (res) {
        if( this.userUpdate.roles[0].roleId !== this.dataUser.role){
          await this.setUserRole({
            userId: this.userUpdate.userId,
            roleId: this.dataUser.role,
          });
        }
        this.showToastSuccess(true, "Profile updated successfully!");
        this.refetchData();
      }
      this.$refs.user.hide();
    },
    handleEditRole(item) {
      this.userUpdate = item;
      this.newUserRole = item.roles[0].roleId;
      this.titleRole = `Manage User Role - ${item.firstName} ${item.lastName}`;
      this.$refs.role.show();
    },
    async handleSubmitChangeUserRole() {
      const res = await this.setUserRole({
        userId: this.userUpdate.userId,
        roleId: this.newUserRole,
      });

      if (res) {
        this.showToastSuccess(true, "User updated successfully!");
        this.$refs.role.hide();
        this.refetchData();
      }
    },
    handleChangeRole(val) {
      this.newUserRole = val;
    }
  },
  setup() {
    const refAdminListsTable = ref(null);
    const totalAdmins = ref(0);
    const currentPage = ref(1);
    const perPage = ref(10);
    const searchQuery = ref(null);

    const dataMeta = computed(() => {
      const localItemsCount = refAdminListsTable.value
        ? refAdminListsTable.value.localItems.length
        : 0;
      return {
        from:
          perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
        to: perPage.value * (currentPage.value - 1) + localItemsCount,
        of: totalAdmins.value,
      };
    });

    const refetchData = () => {
      refAdminListsTable.value.refresh();
    };

    watch([currentPage], () => {
      refetchData();
    });

    watch(
      [searchQuery],
      debounce(() => {
        refetchData();
        // eslint-disable-next-line comma-dangle
      }, 700)
    );

    const { showToastError, showToastSuccess } = useToastHandler();

    return {
      getUserData,

      // Filter
      avatarText,

      showToastError,
      showToastSuccess,

      isRoleSuperAdmin,
      formatDate,
      formatCurrencyBuiltInFunc,
      formatDateTimeLocal,
      formatDateTimeTracking,
      formatDistanceStrict,
      refAdminListsTable,
      formatTime,

      totalAdmins,
      currentPage,
      perPage,
      dataMeta,
      calculateDate18YearsAgo,
      refetchData,
      searchQuery,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.text-title {
  color: #000;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.dark-layout
  .flatpickr-calendar
  .flatpickr-days
  .flatpickr-day:hover:not(.selected):not(.today):not(.startRange):not(
    .endRange
  ) {
  background: #ccc;
}

.pac-container.pac-logo {
  z-index: 1000000 !important;
}
</style>
